import React, { useState } from 'react';
import { InquiryHero, SuccessBanner, PrivacyNote, FormActions, submitInquiry } from './formUtils';
import { Section, TextField, TextareaField, SelectField, MultiSelectField, FileUploadField, ConditionalText, ConsentSection } from './FormFields';
import { CDMO_OPTIONS, HOW_HEARD_OPTIONS, COUNTRY_LIST } from '../../data/partnerOptions';

const initial = {
  fullName: '', company: '', jobTitle: '', email: '', phone: '',
  countryIncorporation: '', countrySubsidiaries: [],
  industrySector: '', industrySectorOther: '', compoundName: '', developmentStage: '', servicesRequired: [],
  targetScale: '', puritySpec: '', puritySpecCustom: '',
  qualityRegulatory: [], qualityRegulatoryOther: '', criticalQualityAttributes: [],
  timeline: '', annualVolume: '', budgetRange: '', additionalContext: '', howHeard: '',
  consentRequired: false, consentOptional: false,
};

export const CDMOForm = () => {
  const [data, setData] = useState(initial);
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [showConsentError, setShowConsentError] = useState(false);

  const set = (k) => (v) => setData((d) => ({ ...d, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!data.consentRequired) { setShowConsentError(true); return; }
    setError('');
    setSubmitting(true);
    try {
      const sections = [
        { title: 'Contact Information', fields: [
          { label: 'Full name', value: data.fullName },
          { label: 'Company / organization', value: data.company },
          { label: 'Job title / role', value: data.jobTitle },
          { label: 'Business email', value: data.email },
          { label: 'Phone number', value: data.phone },
          { label: 'Country of incorporation (Headquarters)', value: data.countryIncorporation },
        ]},
        { title: 'Product & Process Details', fields: [
          { label: 'Industry sector', value: data.industrySector },
          { label: 'Industry sector - specified', value: data.industrySectorOther },
          { label: 'Compound / product name / CAS', value: data.compoundName },
          { label: 'Current development stage', value: data.developmentStage },
          { label: 'Service(s) required', value: data.servicesRequired },
        ]},
        { title: 'Scale & Quality Specifications', fields: [
          { label: 'Target production scale', value: data.targetScale },
          { label: 'Purity specification', value: data.puritySpec },
          { label: 'Custom purity specification', value: data.puritySpecCustom },
          { label: 'Quality / regulatory requirements', value: data.qualityRegulatory },
          { label: 'Quality / regulatory - other specified', value: data.qualityRegulatoryOther },
          { label: 'Critical quality attributes', value: data.criticalQualityAttributes },
        ]},
        { title: 'Timeline & Commercial Outlook', fields: [
          { label: 'Project timeline', value: data.timeline },
          { label: 'Projected annual volume', value: data.annualVolume },
          { label: 'Budget range', value: data.budgetRange },
          { label: 'Additional project context', value: data.additionalContext },
        ]},
        { title: 'Referral', fields: [
          { label: 'How did you hear about LAMKO?', value: data.howHeard },
        ]},
      ];
      await submitInquiry({
        formType: 'cdmo', sections,
        meta: { fullName: data.fullName, companyName: data.company, businessEmail: data.email, country: '' },
        files,
      });
      setSuccess(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(err.message || 'Submission failed. Please try again.');
    }
    setSubmitting(false);
  };

  const reset = () => { setData(initial); setFiles([]); setError(''); setSuccess(false); };

  return (
    <div className="min-h-screen bg-gray-50">
      <InquiryHero
        title="Contract Development & Manufacturing (CDMO)"
        subtitle="Scale-up, process optimization & pilot production"
      />
      <section className="py-10 md:py-14">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 md:p-10">
            {success && <SuccessBanner />}
            {error && <div className="mb-6 bg-red-50 border border-red-200 text-red-800 rounded-md p-4 text-sm" data-testid="error-banner">{error}</div>}
            <form onSubmit={handleSubmit} className="space-y-8">
              <Section title="Contact Information">
                <div className="grid sm:grid-cols-2 gap-5">
                  <TextField name="fullName" label="Full name" required helper="Your first and last name." placeholder="e.g., Dr. Soo-Yeon Kim" value={data.fullName} onChange={set('fullName')} />
                  <TextField name="company" label="Company / organization" required helper="Legal entity or institution name." placeholder="e.g., ABC Co., Ltd." value={data.company} onChange={set('company')} />
                  <TextField name="jobTitle" label="Job title / role" required helper="Your current position." placeholder="e.g., Director of Process Development" value={data.jobTitle} onChange={set('jobTitle')} />
                  <TextField name="email" type="email" label="Business email" required helper="Primary email for project correspondence." placeholder="e.g., sykim@company.com" value={data.email} onChange={set('email')} />
                  <TextField name="phone" type="tel" label="Phone number" helper="Include country code." placeholder="e.g., +82-10-1234-5678" value={data.phone} onChange={set('phone')} />
                </div>
              </Section>

              <Section title="Product & Process Details">
                <SelectField name="industrySector" label="Industry sector" required helper="Primary market segment for this product." options={CDMO_OPTIONS.industrySector} value={data.industrySector} onChange={set('industrySector')} />
                <ConditionalText show={data.industrySector === 'Other (please specify)'} name="industrySectorOther" placeholder="Please describe your industry sector" value={data.industrySectorOther} onChange={set('industrySectorOther')} />
                <TextField name="compoundName" label="Compound / product name / CAS" required helper="Chemical name, product identifier, or CAS number." placeholder="e.g., Ir(ppy)₃ / CAS 94928-86-2" value={data.compoundName} onChange={set('compoundName')} />
                <SelectField name="developmentStage" label="Current development stage" required helper="Where is your process today?" options={CDMO_OPTIONS.developmentStage} value={data.developmentStage} onChange={set('developmentStage')} />
                <MultiSelectField name="servicesRequired" label="Service(s) required" required helper="Select all development and manufacturing services needed." options={CDMO_OPTIONS.servicesRequired} values={data.servicesRequired} onChange={set('servicesRequired')} columns={2} />
              </Section>

              <Section title="Scale & Quality Specifications">
                <SelectField name="targetScale" label="Target production scale" required helper="Anticipated batch size at commercial stage." options={CDMO_OPTIONS.targetScale} value={data.targetScale} onChange={set('targetScale')} />
                <SelectField name="puritySpec" label="Purity specification" required helper="Final product purity requirement." options={CDMO_OPTIONS.puritySpec} value={data.puritySpec} onChange={set('puritySpec')} />
                <ConditionalText show={data.puritySpec === 'Custom specification (describe below)'} name="puritySpecCustom" helper="Please describe your custom purity specification." placeholder="e.g., > 99.999% with defined individual metal limits" value={data.puritySpecCustom} onChange={set('puritySpecCustom')} />
                <MultiSelectField name="qualityRegulatory" label="Quality / regulatory requirements" required helper="Select all applicable quality frameworks." options={CDMO_OPTIONS.qualityRegulatory} values={data.qualityRegulatory} onChange={set('qualityRegulatory')} columns={2} />
                <ConditionalText show={(data.qualityRegulatory || []).includes('Other (please specify)')} name="qualityRegulatoryOther" helper="Please describe the other quality/regulatory framework." placeholder="e.g., Company-specific quality manual" value={data.qualityRegulatoryOther} onChange={set('qualityRegulatoryOther')} />
                <MultiSelectField name="criticalQualityAttributes" label="Critical quality attributes" helper="Key parameters that define product acceptance." options={CDMO_OPTIONS.criticalQualityAttributes} values={data.criticalQualityAttributes} onChange={set('criticalQualityAttributes')} columns={2} />
              </Section>

              <Section title="Timeline & Commercial Outlook">
                <SelectField name="timeline" label="Project timeline" required helper="When do you need first deliverables?" options={CDMO_OPTIONS.timeline} value={data.timeline} onChange={set('timeline')} />
                <SelectField name="annualVolume" label="Projected annual volume" helper="Estimated yearly demand at commercial stage." options={CDMO_OPTIONS.annualVolume} value={data.annualVolume} onChange={set('annualVolume')} />
                <SelectField name="budgetRange" label="Budget range" helper="Approximate investment level for this engagement." options={CDMO_OPTIONS.budgetRange} value={data.budgetRange} onChange={set('budgetRange')} />
                <TextareaField name="additionalContext" label="Additional project context" helper="Describe known process challenges, preferred chemistry, specific equipment needs, regulatory timelines, or any other relevant information." placeholder="e.g., We have an existing 3-step synthesis at 500g scale with 72% overall yield. Main challenge is step 2 selectivity and solvent recovery..." value={data.additionalContext} onChange={set('additionalContext')} />
              </Section>

              <Section title="Document Upload">
                <FileUploadField name="files" label="Upload technical documents" helper="Upload process descriptions, batch records, specifications, COA, SDS, or regulatory filings (PDF, DOC, XLS, CDX - max 25 MB per file)." files={files} onChange={setFiles} />
                <SelectField name="howHeard" label="How did you hear about LAMKO?" helper="Help us understand our reach." options={HOW_HEARD_OPTIONS} value={data.howHeard} onChange={set('howHeard')} />
              </Section>

              <FormActions onReset={reset} isSubmitting={submitting} />
              <ConsentSection variant="customer" consent1={data.consentRequired} consent2={data.consentOptional} onChange1={set('consentRequired')} onChange2={set('consentOptional')} showError={showConsentError} />
              <PrivacyNote />
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

